import { useEffect, useRef, type CSSProperties } from "react";
import { cn } from "~/lib/utils";
import { HERO_SCENES, type HeroBackgroundTheme } from "./scenes";
import "./HeroBackground.css";

// The scenes move slowly, so 30fps looks the same as 60 at half the cost.
const DEFAULT_FPS = 30;
const MAX_PIXEL_RATIO = 2;
// Lens blur on the far layer at the 260px reference height.
const FAR_BLUR_PX = 2.6;

// Monochrome film grain, which also breaks up banding in the dark gradients.
const GRAIN = `url("data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'>" +
    "<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/>" +
    "<feColorMatrix type='saturate' values='0'/></filter>" +
    "<rect width='100%' height='100%' filter='url(#n)'/></svg>"
)}")`;

const BLOOM_STOPS = 14;

/** A radial bloom with a Gaussian falloff. Few or linear stops leave kinks in
 *  the slope that the eye reads as rims against the dark base. */
const bloom = (shape: string, rgb: string, peak: number) => {
  const floor = Math.exp(-4);
  const stops = Array.from({ length: BLOOM_STOPS + 1 }, (_, i) => {
    const t = i / BLOOM_STOPS;
    const alpha = (peak * (Math.exp(-4 * t * t) - floor)) / (1 - floor);
    return `rgb(${rgb} / ${alpha.toFixed(4)}) ${(t * 100).toFixed(1)}%`;
  });
  return `radial-gradient(${shape}, ${stops.join(", ")})`;
};

interface Light {
  rgb: string;
  peak: number;
  /** Centre, as a percentage of the banner. */
  x: number;
  y: number;
  /** Size in container-height units, so previews light like the banner. */
  width: number;
  height: number;
  drift: string;
  driftSeconds: number;
}

// A violet key light upper right, a cool indigo fill from the lower left, a
// faint orchid accent low on the right, and a lavender haze along the top.
const LIGHTS: Light[] = [
  {
    rgb: "151 71 255",
    peak: 0.4,
    x: 86,
    y: 0,
    width: 250,
    height: 190,
    drift: "hero-drift-key",
    driftSeconds: 83,
  },
  {
    rgb: "99 102 241",
    peak: 0.3,
    x: 6,
    y: 105,
    width: 240,
    height: 170,
    drift: "hero-drift-fill",
    driftSeconds: 109,
  },
  {
    rgb: "192 132 252",
    peak: 0.15,
    x: 66,
    y: 96,
    width: 150,
    height: 110,
    drift: "hero-drift-accent",
    driftSeconds: 67,
  },
  {
    rgb: "196 181 253",
    peak: 0.08,
    x: 42,
    y: -8,
    width: 420,
    height: 80,
    drift: "hero-drift-haze",
    driftSeconds: 131,
  },
];

const LIGHT_STYLES: CSSProperties[] = LIGHTS.map((light) => ({
  left: `${light.x}%`,
  top: `${light.y}%`,
  width: `${light.width}cqh`,
  height: `${light.height}cqh`,
  backgroundImage: bloom("closest-side", light.rgb, light.peak),
  ["--hero-drift" as string]: light.drift,
  ["--hero-drift-duration" as string]: `${light.driftSeconds}s`,
}));

// Dims the scene behind the greeting and search box.
const QUIET_CENTER =
  "radial-gradient(ellipse 38% 62% at 50% 62%, rgb(0 0 0 / 0.35), rgb(0 0 0 / 0.35) 35%, #000 100%)";

interface HeroBackgroundProps {
  theme: HeroBackgroundTheme;
  /** Dim the middle, where the banner's text sits. */
  quietCenter?: boolean;
  className?: string;
}

/**
 * A decorative backdrop that fills its positioned parent: slowly drifting CSS
 * lights and grain shared by every theme, under a scene painted on two
 * canvases, a blurred far plane and a sharp near one. Everything animates only
 * while on screen and holds a still frame when the user prefers reduced
 * motion. Each mount starts at a random point in the scene's time, so no two
 * visits look alike.
 */
export const HeroBackground = ({ theme, quietCenter = false, className }: HeroBackgroundProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const farRef = useRef<HTMLCanvasElement>(null);
  const nearRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const farCanvas = farRef.current!;
    const nearCanvas = nearRef.current!;
    const far = farCanvas.getContext("2d");
    const near = nearCanvas.getContext("2d");
    if (!far || !near) {
      throw new Error("HeroBackground could not get a 2D canvas context");
    }
    const scene = HERO_SCENES[theme]();
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const timeOffset = Math.random() * 3600;
    const startedAt = performance.now();
    // A little under the target interval, so rAF jitter never skips a frame.
    const frameInterval = 1000 / ((scene.fps ?? DEFAULT_FPS) * (16 / 15));

    let width = 0;
    let height = 0;
    let pixelRatio = 1;
    let onScreen = true;
    let frame: number | undefined;
    let lastPaint = -Infinity;

    const paint = (now: number) => {
      // The far plane is blurred, so it never needs more than one pixel per
      // CSS pixel.
      far.setTransform(1, 0, 0, 1, 0, 0);
      far.clearRect(0, 0, width, height);
      near.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      near.clearRect(0, 0, width, height);
      scene.draw({ far, near }, width, height, timeOffset + (now - startedAt) / 1000);
    };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (now - lastPaint >= frameInterval) {
        lastPaint = now;
        paint(now);
      }
    };

    const sync = () => {
      const shouldRun = !scene.still && onScreen && !reducedMotion.matches;
      if (shouldRun && frame === undefined) {
        frame = requestAnimationFrame(tick);
      } else if (!shouldRun && frame !== undefined) {
        cancelAnimationFrame(frame);
        frame = undefined;
      }
    };

    const resize = new ResizeObserver(([entry]) => {
      width = entry.contentRect.width;
      height = entry.contentRect.height;
      pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      farCanvas.width = Math.round(width);
      farCanvas.height = Math.round(height);
      farCanvas.style.filter = `blur(${((FAR_BLUR_PX * height) / 260).toFixed(2)}px)`;
      nearCanvas.width = Math.round(width * pixelRatio);
      nearCanvas.height = Math.round(height * pixelRatio);
      // Resizing wipes the canvases; repaint now rather than on the next frame.
      paint(performance.now());
    });
    const visibility = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      rootRef.current!.toggleAttribute("data-offscreen", !onScreen);
      sync();
    });
    const onMotionPreference = () => {
      sync();
      paint(performance.now());
    };

    resize.observe(nearCanvas);
    visibility.observe(nearCanvas);
    reducedMotion.addEventListener("change", onMotionPreference);
    sync();

    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
      resize.disconnect();
      visibility.disconnect();
      reducedMotion.removeEventListener("change", onMotionPreference);
    };
  }, [theme]);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      data-hero-background={theme}
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
      style={{ containerType: "size" }}
    >
      <div className="absolute inset-0 bg-[var(--dt-primitives-color-brand-deep-space)]" />
      {LIGHT_STYLES.map((style, i) => (
        <div key={i} className="hero-light" style={style} />
      ))}
      <div
        className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{ backgroundImage: GRAIN }}
      />
      <div
        // Keyed by theme so a switch fades the new scene in.
        key={theme}
        className="absolute inset-0 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-1000"
        style={quietCenter ? { maskImage: QUIET_CENTER } : undefined}
      >
        <canvas ref={farRef} className="absolute inset-0 size-full" />
        <canvas ref={nearRef} className="absolute inset-0 size-full" />
      </div>
    </div>
  );
};
